if (!customElements.get('brew-floating-reserve')) {
  customElements.define(
    'brew-floating-reserve',
    class BrewFloatingReserve extends HTMLElement {
      connectedCallback() {
        this.section = document.querySelector(this.dataset.sectionSelector);
        this.lastScrollY = window.scrollY;
        this.isCompact = false;
        this.isInputFocused = false;
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);
        this.onInputFocus = this.onInputFocus.bind(this);
        this.onInputBlur = this.onInputBlur.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.mobileQuery = window.matchMedia('(max-width: 989px)');

        if (window.Shopify && Shopify.designMode) {
          this.reveal(false);
          this.syncTop(false);
          return;
        }

        if (!this.section) return;

        this.bindHeaderObserver();
        this.bindInputFocus();
        this.update();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.headerObserver?.disconnect();
        this.unbindInputFocus();
        this.unlockHeaderHidden();
      }

      bindHeaderObserver() {
        const sectionHeader = document.querySelector('.section-header');
        if (!sectionHeader || typeof MutationObserver === 'undefined') return;

        // Only sync fade-out for menu/cart — do not re-run scroll/compact logic,
        // or header class changes will keep collapsing the minimised state.
        this.headerObserver = new MutationObserver(() => this.syncObscured());
        this.headerObserver.observe(sectionHeader, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      bindInputFocus() {
        this.emailInput = this.querySelector('input[type="email"]');
        if (!this.emailInput) return;

        this.emailInput.addEventListener('focus', this.onInputFocus);
        this.emailInput.addEventListener('blur', this.onInputBlur);
      }

      unbindInputFocus() {
        this.emailInput?.removeEventListener('focus', this.onInputFocus);
        this.emailInput?.removeEventListener('blur', this.onInputBlur);
        this.unbindViewportWatch();
      }

      bindViewportWatch() {
        if (this.viewportBound) return;
        this.viewportBound = true;
        window.visualViewport?.addEventListener('resize', this.onViewportChange);
        window.visualViewport?.addEventListener('scroll', this.onViewportChange);
      }

      unbindViewportWatch() {
        if (!this.viewportBound) return;
        this.viewportBound = false;
        window.visualViewport?.removeEventListener('resize', this.onViewportChange);
        window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
      }

      onViewportChange() {
        if (!this.isInputFocused) return;
        this.revealEmailFocusState();
      }

      onInputFocus() {
        if (!this.mobileQuery.matches) return;

        this.isInputFocused = true;
        this.classList.add('is-input-focused');
        this.bindViewportWatch();
        this.lockHeaderHidden();
        this.revealEmailFocusState();
      }

      onInputBlur() {
        // Allow focus to move to the Reserve button without exiting focus mode.
        requestAnimationFrame(() => {
          if (this.contains(document.activeElement)) return;

          this.isInputFocused = false;
          this.classList.remove('is-input-focused');
          this.unbindViewportWatch();
          this.unlockHeaderHidden();
          this.update();
        });
      }

      revealEmailFocusState() {
        this.isCompact = false;
        this.reveal(false);
        this.syncTop(false);
        this.syncObscured();
      }

      lockHeaderHidden() {
        const stickyHeader = document.querySelector('sticky-header');
        const sectionHeader = document.querySelector('.section-header');
        if (!sectionHeader) return;

        if (stickyHeader) {
          stickyHeader.preventReveal = true;
          stickyHeader.hide?.();
        }

        sectionHeader.classList.add('animate', 'shopify-section-header-hidden', 'shopify-section-header-sticky');
      }

      unlockHeaderHidden() {
        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader) {
          stickyHeader.preventReveal = false;
        }
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      isMobilePanelOpen() {
        if (!this.mobileQuery.matches) return false;
        const sectionHeader = document.querySelector('.section-header');
        return Boolean(
          sectionHeader?.classList.contains('menu-open') || sectionHeader?.classList.contains('cart-open')
        );
      }

      update() {
        if (!this.section) return;

        if (this.isInputFocused && this.mobileQuery.matches) {
          this.lockHeaderHidden();
          this.revealEmailFocusState();
          return;
        }

        const sectionBottom = this.section.getBoundingClientRect().bottom;
        const scrolledPast = sectionBottom <= 0;
        const scrollY = window.scrollY;
        const scrollDelta = scrollY - this.lastScrollY;

        if (Math.abs(scrollDelta) > 2) {
          this.isCompact = scrollDelta > 0;
          this.lastScrollY = scrollY;
        }

        if (!scrolledPast) {
          this.hide();
          return;
        }

        this.reveal(this.isCompact);
        this.syncTop(this.isCompact);
        this.syncObscured();
      }

      syncObscured() {
        if (this.isInputFocused && this.mobileQuery.matches) {
          this.classList.remove('is-obscured');
          this.setAttribute('aria-hidden', 'false');
          return;
        }

        const obscured = this.isMobilePanelOpen();
        this.classList.toggle('is-obscured', obscured);
        if (obscured) {
          this.setAttribute('aria-hidden', 'true');
        } else if (this.classList.contains('is-visible')) {
          this.setAttribute('aria-hidden', 'false');
        }
      }

      syncTop(compact) {
        if (!this.mobileQuery.matches) {
          this.style.removeProperty('--brew-floating-top');
          return;
        }

        if (this.isInputFocused) {
          const viewportOffset = window.visualViewport?.offsetTop || 0;
          this.style.setProperty('--brew-floating-top', `calc(${viewportOffset}px + 1rem)`);
          return;
        }

        if (compact) {
          this.style.setProperty('--brew-floating-top', '1rem');
          return;
        }

        const headerBar = document.querySelector('.section-header .header-wrapper');
        if (!headerBar) {
          this.style.setProperty('--brew-floating-top', '1rem');
          return;
        }

        const sectionHeader = document.querySelector('.section-header');
        const headerHidden = sectionHeader?.classList.contains('shopify-section-header-hidden');
        if (headerHidden) {
          this.style.setProperty('--brew-floating-top', '1rem');
          return;
        }

        const bottom = Math.max(0, headerBar.getBoundingClientRect().bottom);
        this.style.setProperty('--brew-floating-top', `calc(${bottom}px + 1rem)`);
      }

      reveal(compact) {
        this.classList.add('is-visible');
        this.classList.toggle('is-compact', Boolean(compact));
        if (!this.classList.contains('is-obscured')) {
          this.setAttribute('aria-hidden', 'false');
        }
      }

      hide() {
        this.classList.remove('is-visible', 'is-compact', 'is-obscured', 'is-input-focused');
        this.isCompact = false;
        this.setAttribute('aria-hidden', 'true');
        this.style.removeProperty('--brew-floating-top');
      }
    }
  );
}
